<template>
  <div
    class="relative bg-gradient-to-br from-white via-gray-50 to-logo-color/5 py-20 md:py-32 overflow-hidden"
  >
    <!-- Background Decorations -->
    <div class="absolute inset-0 pointer-events-none">
      <div
        class="absolute top-20 left-20 w-80 h-80 bg-logo-color/5 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute bottom-10 right-10 w-96 h-96 bg-logo-color/5 rounded-full blur-3xl"
      ></div>
    </div>

    <div class="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <!-- Header Section -->
      <div class="text-center max-w-3xl mx-auto mb-16" data-aos="fade-up">
        <span
          class="inline-flex items-center gap-2 px-4 py-2 bg-logo-color/10 rounded-full mb-6"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 text-logo-color"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
            />
          </svg>
          <span
            class="text-sm font-semibold text-logo-color uppercase tracking-wider"
            >Our Core Values</span
          >
        </span>

        <h2
          class="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight"
        >
          Ethical, Innovative &
          <span class="text-logo-color relative">
            Transformative
            <svg
              class="absolute -bottom-2 left-0 w-full"
              height="8"
              viewBox="0 0 200 8"
              fill="none"
            >
              <path
                d="M1 5.5C47.6667 2.16667 141.4 -2.1 199 5.5"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                class="text-logo-color/30"
              />
            </svg>
          </span>
          <br />Youth Leadership
        </h2>

        <p
          class="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto"
        >
          Youth Ally's vision is an inclusive and prosperous Africa, fostered by
          ethical, innovative, skilled and transformative youth leadership.
        </p>
      </div>

      <!-- Cards Grid -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        <!-- Card 1 - Respect -->
        <div
          class="group bg-white rounded-3xl p-8 transition-all duration-300 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.2)] hover:-translate-y-2"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <div
            class="w-14 h-14 bg-logo-color/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-logo-color group-hover:scale-110 transition-all duration-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-7 w-7 text-logo-color group-hover:text-white transition-colors"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </div>

          <h3 class="text-xl font-bold text-gray-900 mb-3">Respect</h3>
          <p class="text-gray-600 leading-relaxed text-sm mb-6">
            We treat all people with respect and dignity. We believe every young
            person has inherent worth and potential.
          </p>

          <div
            class="flex items-center gap-2 text-logo-color font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <span>Learn more</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 group-hover:translate-x-1 transition-transform"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </div>
        </div>

        <!-- Card 2 - Learning -->
        <div
          class="group bg-white rounded-3xl p-8 transition-all duration-300 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.2)] hover:-translate-y-2 md:translate-y-6"
          data-aos="fade-up"
          data-aos-delay="200"
        >
          <div
            class="w-14 h-14 bg-logo-color/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-logo-color group-hover:scale-110 transition-all duration-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-7 w-7 text-logo-color group-hover:text-white transition-colors"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>

          <h3 class="text-xl font-bold text-gray-900 mb-3">Learning</h3>
          <p class="text-gray-600 leading-relaxed text-sm mb-6">
            We challenge each other to strive for excellence and to continually
            learn and grow to reach full potential.
          </p>

          <div
            class="flex items-center gap-2 text-logo-color font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <span>Learn more</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 group-hover:translate-x-1 transition-transform"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </div>
        </div>

        <!-- Card 3 - Innovation -->
        <div
          class="group bg-white rounded-3xl p-8 transition-all duration-300 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.2)] hover:-translate-y-2"
          data-aos="fade-up"
          data-aos-delay="300"
        >
          <div
            class="w-14 h-14 bg-logo-color/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-logo-color group-hover:scale-110 transition-all duration-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-7 w-7 text-logo-color group-hover:text-white transition-colors"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
          </div>

          <h3 class="text-xl font-bold text-gray-900 mb-3">
            Innovation & Inclusivity
          </h3>
          <p class="text-gray-600 leading-relaxed text-sm mb-6">
            We embrace innovation and bold creativity. We believe in diversity
            and encourage unique perspectives.
          </p>

          <div
            class="flex items-center gap-2 text-logo-color font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <span>Learn more</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 group-hover:translate-x-1 transition-transform"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </div>
        </div>
      </div>

      <!-- Bottom CTA -->
      <div class="text-center mt-16" data-aos="fade-up" data-aos-delay="400">
        <router-link
          to="/services"
          class="inline-flex items-center gap-3 px-8 py-4 bg-logo-color text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-logo-color/25 hover:-translate-y-0.5 transition-all duration-300"
        >
          <span>Explore Our Programs</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M14 5l7 7m0 0l-7 7m7-7H3"
            />
          </svg>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WhyChooseUs",
};
</script>

<style scoped>
/* Modern minimalist design - no additional styles needed */
</style>
